import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getLeaderboard } from '@/db';
import type { LeaderboardEntry } from '@/db';
import { Trophy, Medal, Award, User } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function LeaderboardView() {
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    setIsLoading(true);
    const data = await getLeaderboard(undefined, 50);
    setLeaderboard(data);
    setIsLoading(false);
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="w-6 h-6 text-yellow-400" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-300" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-600" />;
      default:
        return <span className="text-slate-400 font-mono w-6 text-center">{rank}</span>;
    }
  };

  const getRankStyle = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-yellow-500/10 border-yellow-500/30';
      case 2:
        return 'bg-slate-400/10 border-slate-400/30';
      case 3:
        return 'bg-amber-600/10 border-amber-600/30';
      default:
        return 'bg-slate-800/30 border-slate-700/30';
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-white mb-4">排行榜</h2>

      <Tabs defaultValue="weekly" className="w-full">
        <TabsList className="grid w-full grid-cols-3 bg-slate-800">
          <TabsTrigger value="weekly" className="data-[state=active]:bg-slate-700">本周榜</TabsTrigger>
          <TabsTrigger value="total" className="data-[state=active]:bg-slate-700">总榜</TabsTrigger>
          <TabsTrigger value="winrate" className="data-[state=active]:bg-slate-700">胜率榜</TabsTrigger>
        </TabsList>

        <TabsContent value="weekly" className="mt-4">
          {isLoading ? (
            <div className="text-center py-8 text-slate-400">加载中...</div>
          ) : leaderboard.length === 0 ? (
            <div className="text-center py-8 text-slate-400">
              <Trophy className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>本周暂无数据</p>
              <p className="text-sm">参加锦标赛赢取积分</p>
            </div>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, index) => (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`flex items-center gap-4 p-4 rounded-xl border ${getRankStyle(entry.rank)}`}
                >
                  <div className="flex-shrink-0">
                    {getRankIcon(entry.rank)}
                  </div>
                  
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white truncate">{entry.name}</p>
                    <p className="text-xs text-slate-400">ID: {entry.profileId.slice(0, 8)}</p>
                  </div>
                  
                  <div className="text-right">
                    <p className="text-amber-400 font-bold text-lg">{entry.score.toLocaleString()}</p>
                    <p className="text-xs text-slate-500">积分</p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="total" className="mt-4">
          <div className="text-center py-8 text-slate-400">
            <p>总榜功能开发中...</p>
          </div>
        </TabsContent>

        <TabsContent value="winrate" className="mt-4">
          <div className="text-center py-8 text-slate-400">
            <p>胜率榜功能开发中...</p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
